import React, { useEffect, useReducer, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";

import Spinner from "../Components/Spinner";
import { ToastComponent } from "../Components/ToastComponent";
import DeleteModal from "../Components/Modals/Delete-modal";
import EmptyData from "../../components/EmptyData";

import { transportsClientsService } from "../../Services/transportsClients.service";
import { paginationService } from "../../Services/pagination.service";
import { toDateISOStringFormatD, toDateTime } from "../../Services/string.service";

import { SiAddthis } from "react-icons/si";
import { BiEditAlt, BiTrash } from "react-icons/bi";
import {
  GridComponent,
  Page,
  Inject,
  ColumnDirective,
  ColumnsDirective,
  Filter,
} from "@syncfusion/ej2-react-grids";

const formInitialValues = {
  companyId: 0,
  companyName: "",
  cnpj: "",
  phone1: "",
  phone2: "",
  email: "",
  login: "",
  lastAccess: "",
  sendNonConformityEmail: false,
  sendConformityEmail: false,
  sendCopy: false,
};

const normalizeFlag = (value) => value === "S" || value === true;
const flagPayloadValue = (value) => (value ? "S" : "N");

const buildApiErrorMessage = (response, fallback) => {
  if (Array.isArray(response?.validations) && response.validations.length > 0) {
    return response.validations
      .map((validation) => validation.message)
      .filter(Boolean)
      .join(" ");
  }
  return response?.message || fallback;
};

const onlyDigits = (value) => (value || "").toString().replace(/\D/g, "");

const formatPhoneNumber = (value) => {
  const cleaned = onlyDigits(value).slice(0, 11);

  if (cleaned.length === 0) return "";
  if (cleaned.length <= 2) return cleaned;
  if (cleaned.length <= 6) return `(${cleaned.slice(0, 2)}) ${cleaned.slice(2)}`;
  if (cleaned.length <= 10) {
    return `(${cleaned.slice(0, 2)}) ${cleaned.slice(2, 6)}-${cleaned.slice(6)}`;
  }
  return `(${cleaned.slice(0, 2)}) ${cleaned.slice(2, 3)} ${cleaned.slice(3, 7)}-${cleaned.slice(7)}`;
};

const formatCnpj = (value) => {
  const cleaned = onlyDigits(value).slice(0, 14);

  if (cleaned.length <= 2) return cleaned;
  if (cleaned.length <= 5) return `${cleaned.slice(0, 2)}.${cleaned.slice(2)}`;
  if (cleaned.length <= 8) {
    return `${cleaned.slice(0, 2)}.${cleaned.slice(2, 5)}.${cleaned.slice(5)}`;
  }
  if (cleaned.length <= 12) {
    return `${cleaned.slice(0, 2)}.${cleaned.slice(2, 5)}.${cleaned.slice(5, 8)}/${cleaned.slice(8)}`;
  }
  return `${cleaned.slice(0, 2)}.${cleaned.slice(2, 5)}.${cleaned.slice(5, 8)}/${cleaned.slice(8, 12)}-${cleaned.slice(12)}`;
};

const formReducer = (state, event) => {
  if (event.reset) {
    return formInitialValues;
  }
  return {
    ...state,
    [event.name]: event.value,
  };
};

export default function FretamentoClients() {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch({ type: "title", value: t("TRANSPORT_CLIENTS.PAGE_TITLE") });
  }, [dispatch, t]);

  const [data, setData] = useState([]);
  const [formData, setFormData] = useReducer(formReducer, formInitialValues);
  const [isPageLoading, setIsPageLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState({});
  const [toastParams, setToastParams] = useState("");
  const [errors, setErrors] = useState({});
  const [errorMessage, setErrorMessage] = useState("");
  const [isDeleting, setIsDeleting] = useState({ show: false, itemName: "" });

  // Altura e paginação dinâmicos
  const [pageSize, setPageSize] = useState(15);
  const gridAnchorRef = useRef(null);

  const ROW_HEIGHT = 30;
  const HEADER_PAGER_BUDGET = 100;
  const BOTTOM_MARGIN = 30;
  const MIN_PAGE = 5;
  const MAX_PAGE = 100;

  const handleCloseModal = () => {
    setIsDeleting({ show: false, itemName: "" });
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (type === "checkbox") {
      setFormData({ name, value: checked });
      return;
    }

    if (name === "phone1" || name === "phone2" || name === "cnpj") {
      setFormData({ name, value: onlyDigits(value) });
      return;
    }

    if (name === "email") {
      setFormData({ name, value: value.replace(/\s/g, "").toLowerCase() });
      return;
    }

    setFormData({ name, value });
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.companyName?.trim() && !editingItem.companyName) {
      newErrors.companyName = t("TRANSPORT_CLIENTS.COMPANY_NAME_REQUIRED");
    }

    if (!formData?.cnpj) {
      newErrors.cnpj = t("TRANSPORT_CLIENTS.CNPJ_REQUIRED");
    } else if (formData.cnpj.length !== 14) {
      newErrors.cnpj = t("TRANSPORT_CLIENTS.CNPJ_INVALID");
    }

    if (!formData?.email) {
      newErrors.email = t("TRANSPORT_CLIENTS.EMAIL_REQUIRED");
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = t("TRANSPORT_CLIENTS.EMAIL_INVALID");
    }

    return newErrors;
  };

  const onNew = () => {
    setFormData({ reset: true });
    setEditingItem({});
    setErrors({});
    setShowForm(true);
  };

  const onEdit = (item) => {
    setEditingItem(item);
    setFormData({ name: "companyId", value: item.companyId });
    setFormData({ name: "companyName", value: item.companyName });
    setFormData({ name: "cnpj", value: onlyDigits(item.cnpj) });
    setFormData({ name: "phone1", value: onlyDigits(item.phone1) });
    setFormData({ name: "phone2", value: onlyDigits(item.phone2) });
    setFormData({ name: "email", value: (item.email || "").toLowerCase() });
    setFormData({ name: "login", value: item.login || "" });
    setFormData({ name: "lastAccess", value: toDateISOStringFormatD(item.lastAccess) });
    setFormData({
      name: "sendNonConformityEmail",
      value: normalizeFlag(item.sendNonConformityEmail),
    });
    setFormData({
      name: "sendConformityEmail",
      value: normalizeFlag(item.sendConformityEmail),
    });
    setFormData({ name: "sendCopy", value: normalizeFlag(item.sendCopy) });
    setErrors({});
    setShowForm(true);
  };

  const onCancel = () => {
    setFormData({ reset: true });
    setEditingItem({});
    setErrors({});
    setShowForm(false);
  };

  const onDelete = (item) => {
    setIsDeleting({
      show: true,
      itemName: item.companyName,
      itemRemoveId: item.companyId,
    });
  };

  const handleRemove = async (companyId) => {
    try {
      const response = await transportsClientsService.deleteClient(companyId);
      if (!response || response?.error) {
        setToastParams(
          JSON.stringify({
            show: true,
            title: t("COMMON.ERROR"),
            body: buildApiErrorMessage(response, t("TRANSPORT_CLIENTS.DELETE_ERROR")),
            color: "Danger",
          })
        );
        return;
      }

      setToastParams(
        JSON.stringify({
          show: true,
          title: t("COMMON.SUCCESS"),
          body: t("TRANSPORT_CLIENTS.DELETE_SUCCESS"),
          color: "Success",
        })
      );
    } catch (error) {
      setToastParams(
        JSON.stringify({
          show: true,
          title: t("COMMON.ERROR"),
          body: t("TRANSPORT_CLIENTS.DELETE_ERROR"),
          color: "Danger",
        })
      );
    } finally {
      handleCloseModal();
      initialize();
    }
  };

  const initialize = async () => {
    setIsPageLoading(true);
    try {
      const response = await transportsClientsService.fetchClients();

      if (!response || (Array.isArray(response) && response.length === 0)) {
        setErrorMessage(t("TRANSPORT_CLIENTS.NO_RECORDS_FOUND"));
        setData([]);
        return;
      }

      setData(Array.isArray(response) ? response : [response]);
      setErrorMessage("");
    } catch (error) {
      setErrorMessage(t("TRANSPORT_CLIENTS.LOAD_ERROR"));
    } finally {
      setIsPageLoading(false);
    }
  };

  const onSave = async () => {
    setErrors(validateForm());

    setIsSaving(true);
    const isEditing = editingItem?.companyId > 0;

    const payload = {
      CompanyId: editingItem?.companyId || 0,
      CompanyName: formData.companyName ?? editingItem.companyName ?? "",
      Cnpj: formData.cnpj ?? editingItem.cnpj ?? "",
      Phone1: formData.phone1 ?? editingItem.phone1 ?? "",
      Phone2: formData.phone2 ?? editingItem.phone2 ?? "",
      Email: formData.email ?? editingItem.email ?? "",
      SendNonConformityEmail: flagPayloadValue(formData.sendNonConformityEmail),
      SendConformityEmail: flagPayloadValue(formData.sendConformityEmail),
      SendCopy: flagPayloadValue(formData.sendCopy),
    };

    try {
      const response = await transportsClientsService.saveClient(payload);
      if (!response || response?.error) {
        setToastParams(
          JSON.stringify({
            show: true,
            title: t("COMMON.ERROR"),
            body: buildApiErrorMessage(response, t("TRANSPORT_CLIENTS.SAVE_ERROR")),
            color: "Danger",
          })
        );
        return;
      }

      setToastParams(
        JSON.stringify({
          show: true,
          title: t("COMMON.SUCCESS"),
          body: isEditing
            ? t("TRANSPORT_CLIENTS.SAVE_SUCCESS_EDIT")
            : t("TRANSPORT_CLIENTS.SAVE_SUCCESS_NEW"),
          color: "Success",
        })
      );
      setShowForm(false);
      setFormData({ reset: true });
      setEditingItem({});
      setErrors({});
      initialize();
    } catch (error) {
      setToastParams(
        JSON.stringify({
          show: true,
          title: t("COMMON.ERROR"),
          body: t("TRANSPORT_CLIENTS.SAVE_ERROR"),
          color: "Danger",
        })
      );
    } finally {
      setIsSaving(false);
    }
  };

  useEffect(() => {
    initialize();
    // eslint-disable-next-line
  }, []);

  // Debounce simples para resize + recálculo ao terminar loading
  useEffect(() => {
    if (!isPageLoading) paginationService.calculateGridMetrics(
      gridAnchorRef,
      BOTTOM_MARGIN,
      HEADER_PAGER_BUDGET,
      ROW_HEIGHT, MIN_PAGE,
      MAX_PAGE,
      data,
      window.innerHeight,
      setPageSize
    );

    let resizeTimeout = null;
    const onResize = () => {
      if (resizeTimeout) window.clearTimeout(resizeTimeout);
      resizeTimeout = window.setTimeout(() => paginationService.calculateGridMetrics(
        gridAnchorRef,
        BOTTOM_MARGIN,
        HEADER_PAGER_BUDGET,
        ROW_HEIGHT, MIN_PAGE,
        MAX_PAGE,
        data,
        window.innerHeight,
        setPageSize
      ), 50);
    };

    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      if (resizeTimeout) window.clearTimeout(resizeTimeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPageLoading]);

  return (
    <div className="container-xxl flex-grow-1 container-p-y">
      <ToastComponent
        params={toastParams}
        closeToast={() => setToastParams(JSON.stringify({ show: false }))}
      />

      <DeleteModal
        itemName={isDeleting.itemName}
        itemRemoveId={isDeleting.itemRemoveId}
        callBackRemove={handleRemove}
        show={isDeleting.show}
        handleClose={handleCloseModal}
      />

      <div ref={gridAnchorRef} />

      {isPageLoading ? (
        <Spinner />
      ) : (
        <div style={showForm ? { display: "none" } : { display: "" }}>
          <div className="d-flex justify-content-between">
            <div className="p-2 card-header">
              <h5>{t("TRANSPORT_CLIENTS.LIST_TITLE")}</h5>
            </div>
            <div className="p-2"></div>
            <div className="p-2">
              <SiAddthis className="icon-new" onClick={onNew} />
            </div>
          </div>

          <div className="card">
            <GridComponent
              dataSource={data}
              allowPaging={true}
              allowFiltering={true}
              filterSettings={{ type: "Menu" }}
              pageSettings={{ pageSize }}
              rowHeight={ROW_HEIGHT}
              height={"auto"}
              locale="pt-BR"
              emptyRecordTemplate={() => <EmptyData />}
              actionComplete={() => {
                setTimeout(() => paginationService.calculateGridMetrics(
                  gridAnchorRef,
                  BOTTOM_MARGIN,
                  HEADER_PAGER_BUDGET,
                  ROW_HEIGHT, MIN_PAGE,
                  MAX_PAGE,
                  data,
                  window.innerHeight,
                  setPageSize
                ), 50);
              }}
            >
              <ColumnsDirective>
                <ColumnDirective
                  field="companyName"
                  headerText={t("TRANSPORT_CLIENTS.COMPANY_NAME")}
                  width="200"
                />
                <ColumnDirective
                  field="login"
                  headerText={t("TRANSPORT_CLIENTS.LOGIN")}
                  width="140"
                />
                <ColumnDirective
                  field="lastAccess"
                  headerText={t("TRANSPORT_CLIENTS.LAST_ACCESS_DATE")}
                  width="180"
                  template={(props) => <span>{toDateTime(props.lastAccess)}</span>}
                />
                <ColumnDirective
                  field="cnpj"
                  headerText={t("TRANSPORT_CLIENTS.CNPJ")}
                  width="100"
                />
                <ColumnDirective
                  field="phone1"
                  headerText={t("TRANSPORT_CLIENTS.PHONE_1")}
                  width="120"
                />
                <ColumnDirective
                  field="phone2"
                  headerText={t("TRANSPORT_CLIENTS.PHONE_2")}
                  width="120"
                />
                <ColumnDirective
                  field="email"
                  headerText={t("TRANSPORT_CLIENTS.EMAIL")}
                  width="200"
                />
                <ColumnDirective
                  headerText={t("TRANSPORT_CLIENTS.ACTIONS")}
                  width="120"
                  template={(props) => (
                    <div style={{ display: "flex", gap: "8px" }}>
                      <BiEditAlt className="icon-button" onClick={() => onEdit(props)} />
                      <BiTrash className="icon-button" onClick={() => onDelete(props)} />
                    </div>
                  )}
                />
              </ColumnsDirective>
              <Inject services={[Page, Filter]} />
            </GridComponent>
          </div>

          {errorMessage && <p className="mt-4">{errorMessage}</p>}
        </div>
      )}

      <div
        className="row py-3"
        style={!showForm ? { display: "none" } : { display: "block" }}
      >
        <div className="col-8">
          <div className="card">
            <h5 className="card-header">
              {editingItem?.companyId > 0
                ? t("TRANSPORT_CLIENTS.EDIT_TITLE")
                : t("TRANSPORT_CLIENTS.NEW_TITLE")}
            </h5>
            <div className="card-header">
              <div className="row mb-3">
                <label className="col-sm-3 col-form-label">
                  {t("TRANSPORT_CLIENTS.COMPANY_NAME")}
                </label>
                <div className="col-sm-9">
                  <input
                    type="text"
                    className={`form-control ${errors.companyName ? "is-invalid" : ""}`}
                    name="companyName"
                    value={formData.companyName || ""}
                    onChange={handleChange}
                    maxLength={150}
                  />
                  {errors.companyName && (
                    <div className="invalid-feedback">{errors.companyName}</div>
                  )}
                </div>
              </div>

              <div className="row mb-3">
                <label className="col-sm-3 col-form-label">
                  {t("TRANSPORT_CLIENTS.PHONE_1")}
                </label>
                <div className="col-sm-4">
                  <input
                    type="text"
                    className="form-control"
                    name="phone1"
                    value={formatPhoneNumber(formData.phone1)}
                    onChange={handleChange}
                    maxLength={17}
                  />
                </div>
                <label className="col-sm-2 col-form-label">
                  {t("TRANSPORT_CLIENTS.PHONE_2")}
                </label>
                <div className="col-sm-3">
                  <input
                    type="text"
                    className="form-control"
                    name="phone2"
                    value={formatPhoneNumber(formData.phone2)}
                    onChange={handleChange}
                    maxLength={17}
                  />
                </div>
              </div>

              <div className="row mb-3">
                <label className="col-sm-3 col-form-label">
                  {t("TRANSPORT_CLIENTS.EMAIL")}
                </label>
                <div className="col-sm-4">
                  <input
                    type="email"
                    className={`form-control ${errors.email ? "is-invalid" : ""}`}
                    name="email"
                    value={formData.email || ""}
                    onChange={handleChange}
                  />
                  {errors.email && <div className="invalid-feedback">{errors.email}</div>}
                </div>
                <label className="col-sm-2 col-form-label">
                  {t("TRANSPORT_CLIENTS.CNPJ")}
                </label>
                <div className="col-sm-3">
                  <input
                    type="text"
                    className={`form-control ${errors.cnpj ? "is-invalid" : ""}`}
                    name="cnpj"
                    value={formatCnpj(formData.cnpj)}
                    onChange={handleChange}
                    maxLength={18}
                  />
                  {errors.cnpj && <div className="invalid-feedback">{errors.cnpj}</div>}
                </div>
              </div>

              <div className="row mb-2">
                <div className="col-sm-12 d-flex align-items-center gap-2">
                  <input
                    type="checkbox"
                    id="sendNonConformityEmail"
                    name="sendNonConformityEmail"
                    checked={Boolean(formData.sendNonConformityEmail)}
                    onChange={handleChange}
                  />
                  <label htmlFor="sendNonConformityEmail" className="mb-0">
                    {t("TRANSPORT_CLIENTS.SEND_NON_CONFORMITY_EMAIL")}
                  </label>
                </div>
              </div>
              <div className="row mb-2">
                <div className="col-sm-12 d-flex align-items-center gap-2">
                  <input
                    type="checkbox"
                    id="sendConformityEmail"
                    name="sendConformityEmail"
                    checked={Boolean(formData.sendConformityEmail)}
                    onChange={handleChange}
                  />
                  <label htmlFor="sendConformityEmail" className="mb-0">
                    {t("TRANSPORT_CLIENTS.SEND_CONFORMITY_EMAIL")}
                  </label>
                </div>
              </div>
              <div className="row mb-3">
                <div className="col-sm-12 d-flex align-items-center gap-2">
                  <input
                    type="checkbox"
                    id="sendCopy"
                    name="sendCopy"
                    checked={Boolean(formData.sendCopy)}
                    onChange={handleChange}
                  />
                  <label htmlFor="sendCopy" className="mb-0">
                    {t("TRANSPORT_CLIENTS.SEND_COPY")}
                  </label>
                </div>
              </div>

              <div className="row justify-content-end" style={{ textAlign: "end" }}>
                <div className="col-ls-8 col-md-8 col-xs-12 mb-3">
                  <button
                    type="button"
                    className="btn btn-warning"
                    style={{ marginRight: "10px" }}
                    onClick={onCancel}
                  >
                    {t("TRANSPORT_CLIENTS.CANCEL")}
                  </button>

                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={onSave}
                    disabled={isSaving}
                  >
                    {t("TRANSPORT_CLIENTS.SAVE")}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
